import React, {useEffect, useRef, useState} from "react";
import { Link, useParams } from "react-router-dom";
import { Container, Spinner } from "react-bootstrap";
import { ArrowLeft } from "react-bootstrap-icons";

import articlesData, { loadGuideHtml } from "../articles-html/articlesData";
import { routes } from "../../utils";
import FadeWrapper from "../../components/FadeWrapper";
import formatDate from "../../components/user-resources/utils/formatDate";
import {isDev} from "../../utils/env";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ResourcesTableOfContents from "../../components/user-resources/ResourcesTableOfContents";
import ReadTime from "../../components/user-resources/ReadTime";
import {Button} from "fearless-common-ui";
import CategorySpans from "../../components/user-resources/CategorySpans";

const IMAGE_BASE = isDev() ? '/static/article-images/guides/': '/website/static/article-images/guides/';

export default function Guide() {
  const { slug } = useParams();
  const contentRef = useRef<HTMLDivElement>(null);

  const article =
    typeof slug === "string" && slug in articlesData ? articlesData[slug] : null;

  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const backToGuidesButton = (
    <Button variant="outline-primary" className="mb-4" href={routes.guides}>
      <ArrowLeft className="me-1" /> Back to guides
    </Button>
  );

  useEffect(() => {
    if (!slug || !article) return;
    let cancelled = false;
    setHtml(null);
    setError(false);
    loadGuideHtml(slug)
      .then((raw) => {
        if (cancelled) return;
        // Fragments were extracted from standalone HTML files with relative paths
        // like `../images/guides/foo.png`. Rewrite to the deployed public path.
        setHtml(raw.replace(/\.\.\/images\/guides\//g, IMAGE_BASE));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, article]);

  if (!article) {
    return (
      <FadeWrapper>
        <Container className="py-5 text-center">
          <h2 className="mb-3">Guide not found</h2>
          <Link to={routes.guides}>← Back to guides</Link>
        </Container>
      </FadeWrapper>
    );
  }

  return (
    <FadeWrapper>
      <div className="py-5 text-white text-center" style={{ background: "#1d446b" }}>
        <CategorySpans categories={article.categories} />
        <h1 className="fw-bold fs-2">{article.title}</h1>
        <time className="d-inline-flex opacity-75" dateTime={article.date}>{formatDate(article.date)}</time>
        <br/>
        {html && <ReadTime variant="badge" text={html} />}
      </div>

      <Container className="py-5" style={{ maxWidth: 1000 }}>
        {error ? (
          <p className="text-danger">Failed to load this guide. Please try again.</p>
        ) : html === null ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading…</span>
            </Spinner>
          </div>
        ) : (
          <Row>
            <Col sm={3}>
              <div className="sticky-top sticky-top-offset">
                {backToGuidesButton}
                <ResourcesTableOfContents articleDep={html} contentRef={contentRef} />
              </div>
            </Col>
            <Col>
              <article
                className="guide-content"
                ref={contentRef}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </Col>
          </Row>
        )}
      </Container>

      <style>{`
        .guide-content h2 { margin-top: 2rem; margin-bottom: 0.75rem; font-size: 1.4rem; }
        .guide-content p { margin-bottom: 1rem; line-height: 1.7; }
        .guide-content img {
          display: block;
          max-width: 100%;
          height: auto;
          margin: 1.25rem auto;
          border-radius: 6px;
          box-shadow: 0 0.25rem 0.75rem rgba(0,0,0,0.08);
        }
      `}</style>
    </FadeWrapper>
  );
}
